import React from "react";
import { Box, Grid } from "@mui/material";
import {
  CardPromptType,
  PromptType,
  TextValuePairType,
} from "../../../store/cardPrompt/types";
import { FormikProps, useFormikContext } from "formik";
import KeyValueEditor from "../../../common/KeyValueEditor";
import produce from "immer";
import { useDispatch } from "react-redux";
import { setPromptEntry } from "../../../store/cardPrompt/cardPromptSlice";
import TextInput from "../../../common/formElements/TextInput";
import SelectDropDown from "../../../common/formElements/SelectDropDown";

interface CardPromptFormProps {
  namespace: string;
  prompt: PromptType;
  cardBinIndex: number;
  promptIndex: number;
}

const availableLanguages = ["en"];
const availableTypes = ["AMOUNT", "NUMBER", "TEXT", "OPTIONS"];
const availableConditions = [
  { "": "None" },
  "BP_ODOMETER_REQUIRED",
  "BP_ORDERNO_REQUIRED", 
  "BP_VEHICLE_ID_REQUIRED",
  "BP_FLEET_ID_REQUIRED",
  "BP_DRIVER_ID_REQUIRED",
  "FISERV_ODOMETER_REQUIRED",
  "FISERV_ORDERNO_REQUIRED",
  "FISERV_REGISTRATION_NUMBER_REQUIRED",
  "FISERV_PERSONAL_PURCHASE_REQUIRED",
  "FISERV_PIN_SET_REQUIRED"
];

const CardPromptForm = ({
  namespace,
  prompt,
  promptIndex,
  cardBinIndex,
}: CardPromptFormProps) => {
  const formikProps = useFormikContext<FormikProps<CardPromptType>>();
  const dispatch = useDispatch();

  const addOption = () => {
    const newEntry: TextValuePairType = {
      Text: "",
      Value: "",
    };
    const options = produce(
      prompt.Options ?? [],
      (draft: TextValuePairType[]) => {
        draft.push(newEntry);
      }
    );
    const newPrompt = produce(prompt, (draft) => {
      draft.Options = options;
    });
    formikProps.setFieldValue(namespace, newPrompt);
    dispatch(
      setPromptEntry({
        prompt: newPrompt,
        promptIndex,
        cardBinIndex,
      })
    );
  };

  const removeOption = (optionIndex: number) => {
    const options = produce(
      prompt.Options ?? [],
      (draft: TextValuePairType[]) => {
        draft.splice(optionIndex, 1);
      }
    );
    const newPrompt = produce(prompt, (draft) => {
      draft.Options = options;
    });
    formikProps.setFieldValue(namespace, newPrompt);
    dispatch(
      setPromptEntry({
        prompt: newPrompt,
        promptIndex,
        cardBinIndex,
      })
    );
  };

  return (
    <Grid
      container
      spacing={2}
      style={{
        width: "100%",
        background: "#fafafa",
        border: "1px solid #efefef",
        borderRadius: "5px",
        marginBottom: 5,
        padding: "10px",
      }}
    >
      <Grid item xs={12} md={4}>
        <SelectDropDown
          fieldname={`${namespace}.Type`}
          label="Type"
          options={availableTypes}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SelectDropDown
          fieldname={`${namespace}.Language`}
          label="Language"
          options={availableLanguages}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput fieldname={`${namespace}.Tag`} label="Tag" />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput fieldname={`${namespace}.Label`} label="Label" />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput fieldname={`${namespace}.Hint`} label="Hint" />
      </Grid>
      <Grid item xs={12} md={4}>
        <SelectDropDown
          fieldname={`${namespace}.Condition`}
          label="Condition"
          options={availableConditions}
        />
      </Grid>
      {prompt.Type === "TEXT" ? (
        <Grid item xs={12} md={4}>
          <TextInput
            fieldname={`${namespace}.MinCharacters`}
            label="Min Characters"
          />
        </Grid>
      ) : null}
      {prompt.Type === "TEXT" ? (
        <Grid item xs={12} md={4}>
          <TextInput
            fieldname={`${namespace}.MaxCharacters`}
            label="Max Characters"
          />
        </Grid>
      ) : null}
      {prompt.Type === "OPTIONS" ? (
        <Grid item xs={12} md={4}>
          <Box mr={2}>
            <KeyValueEditor
              namespace={`${namespace}.Options`}
              entries={prompt.Options}
              onAdd={addOption}
              onDelete={removeOption}
            />
          </Box>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default CardPromptForm;
